import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import useResizeObserver from '@alltrails/core/hooks/useResizeObserver';
import IconButtonWithTooltip from '@alltrails/denali/components/IconButtonWithTooltip';
import IconRenderer, { IconDefinition } from '@alltrails/denali/components/IconRenderer';
import CloseCircle from '@alltrails/denali/icons/CloseCircle';
import { SIZE_CONTROLS_SM, SPACE_100 } from '@alltrails/denali/tokens';
import EditOptions from '@alltrails/maps/components/EditOptions/EditOptions';
import styles from './BigButton.module.scss';

type BigButtonProps = {
  bottomContent?: ReactNode;
  disabled?: boolean;
  icon?: IconDefinition<'orientation' | 'color'>;
  iconBackgroundColor?: string;
  iconElement?: ReactNode;
  isActive?: boolean;
  isDragOverlay?: boolean;
  onClick: () => void;
  onEditOptionClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onRemove?: () => void;
  selected?: boolean;
  shouldHideBorder?: boolean;
  shouldTruncate?: boolean;
  showOptionsMenu?: boolean;
  subtext?: ReactNode;
  text: ReactNode;
  textTwoLines?: boolean;
};

const HEIGHT = 68;

// This will be used to build the Segment and Waypoint components from the designs below:
// https://www.figma.com/design/4r4szI3Prvl4vxYih8PPod/BYOT-Web---Handoff?node-id=286-51688&m=dev
const BigButton = ({
  bottomContent,
  disabled = false,
  icon,
  iconBackgroundColor,
  iconElement = null,
  text,
  subtext,
  isActive,
  isDragOverlay,
  selected,
  onClick,
  onRemove,
  shouldTruncate,
  onEditOptionClick,
  onMouseEnter,
  onMouseLeave,
  shouldHideBorder,
  showOptionsMenu,
  textTwoLines = false
}: BigButtonProps) => {
  const intl = useIntl();
  const tooltipId = useId();
  const bottomContentRef = useRef<HTMLDivElement>(null);

  const [bottomContentHeight, setBottomContentHeight] = useState(0);

  const resizeBottomContent = useCallback((entries: ResizeObserverEntry[]) => {
    const { height } = entries[0].target.getBoundingClientRect();
    setBottomContentHeight(height);
  }, []);
  const [observe, disconnect] = useResizeObserver(resizeBottomContent);

  useEffect(() => {
    if (bottomContentRef.current) {
      observe(bottomContentRef.current);
    }
    return () => disconnect();
  }, [disconnect, observe]);
  const handleRemove = useCallback(
    e => {
      e.stopPropagation();
      onRemove?.();
    },
    [onRemove]
  );

  const contentStyle = {
    paddingBottom: bottomContentHeight,
    paddingRight: showOptionsMenu ? SIZE_CONTROLS_SM + SPACE_100 : 0
  };

  return (
    <div className={styles.container} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <button
        disabled={disabled}
        className={classNames(styles.button, {
          [styles.dragOverlay]: isDragOverlay,
          [styles.selected]: !!selected,
          [styles.active]: !!isActive,
          [styles.hideBorder]: shouldHideBorder
        })}
        style={{ minHeight: HEIGHT }}
        onClick={onClick}
      >
        <div className={styles.contentContainer} style={contentStyle}>
          {icon ? (
            <div className={styles.icon} style={{ backgroundColor: iconBackgroundColor }}>
              <IconRenderer icon={icon} defaults={{ size: 'sm' }} />
            </div>
          ) : (
            iconElement
          )}
          <div className={classNames(styles.content, { [styles.truncated]: shouldTruncate })}>
            <span className={classNames(styles.text, { [styles.twoLines]: textTwoLines })}>{text}</span>
            {subtext && <span className={styles.subtext}>{subtext}</span>}
          </div>
        </div>
      </button>
      {bottomContent && (
        <div ref={bottomContentRef} className={styles.bottomContent}>
          {bottomContent}
        </div>
      )}
      <div className={styles.actionsContainer}>
        {showOptionsMenu && onRemove && onEditOptionClick && (
          <EditOptions onEditClick={onEditOptionClick} onDeleteClick={handleRemove} variant="ghost" />
        )}
        {!showOptionsMenu && onRemove && (
          <IconButtonWithTooltip
            icon={{ Component: CloseCircle }}
            title={intl.formatMessage({ defaultMessage: 'Delete' })}
            tooltipProps={{ text: intl.formatMessage({ defaultMessage: 'Delete' }), hidePointer: true, tooltipPlacement: 'bottom' }}
            onClick={handleRemove}
            uniqueId={`${tooltipId}-remove`}
            variant="ghost"
            size="sm"
          />
        )}
      </div>
    </div>
  );
};

export default BigButton;
